import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatFormField,
  MatLabel,
  MatInput,
  MatError,
  MatPrefix,
} from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { SWISS_POSTAL_CODE_PATTERN } from '../../../../../globals';
import { GeoLocation } from '../../../../types/core';
import { LocationsService } from '../../../../services/locations.service';
import { AttributesApiService } from '../../../../services/attributes-api.service';
import { Institution } from '../../../../types/rest-api';
import { SchoolTypes } from '../../../../types/school';
import { catchError, debounceTime, merge, of, take } from 'rxjs';
import { Auth } from '../../../../core/firebase/auth';
import type { FachkraftUpgradeStep4Snapshot } from '../../fachkraft-upgrade-draft';
import { optionalWebsiteUrlValidator } from '../../../../utils/website-url';
import { ProfileService } from '../../../../services/profile.service';
import type { Profile } from '../../../../types/profile';
import { AddressAutocompleteFieldComponent } from '../../../../components/address-autocomplete-field/address-autocomplete-field.component';
import { revealInvalidFields } from '../../../../core/reveal-invalid-fields';
import { LevelCaptureComponent } from '../../../../components/level-capture/level-capture.component';

function requiredStringArray(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value as unknown;
  if (!Array.isArray(value)) return { required: true };
  const selected = value.filter(v => typeof v === 'string' && v.trim().length);
  return selected.length > 0 ? null : { required: true };
}

function requiredRawCity(group: AbstractControl): ValidationErrors | null {
  const raw = (group as FormGroup).getRawValue?.() as
    | { schoolCity?: string }
    | undefined;
  const city = raw?.schoolCity?.trim?.() ?? '';
  return city.length ? null : { schoolCityRequired: true };
}

@Component({
  selector: 'app-upgrade-school-unit-step4',
  standalone: true,
  templateUrl: './upgrade-school-unit-step4.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatPrefix,
    MatSelect,
    MatOption,
    MatIcon,
    MatButton,
    AddressAutocompleteFieldComponent,
    LevelCaptureComponent,
  ],
})
export class UpgradeSchoolUnitStep4Component implements OnInit {
  private fb = inject(FormBuilder);
  private locationsService = inject(LocationsService);
  private attributesApi = inject(AttributesApiService);
  private destroyRef = inject(DestroyRef);
  private auth = inject(Auth);
  private profileService = inject(ProfileService);
  private host = inject(ElementRef);

  /** Restored from session when resuming the upgrade dialog. */
  initialDraft = input<FachkraftUpgradeStep4Snapshot | null>(null);

  formSubmitted = output<any>();
  goBack = output<void>();
  step4DraftChange = output<FachkraftUpgradeStep4Snapshot>();

  institutions: Institution[] = [];
  schoolTypes = SchoolTypes;
  schoolLocation?: GeoLocation;

  schoolForm = this.fb.group(
    {
      schoolName: ['', [Validators.required]],
      institutionId: [''],
      schoolType: [
        null as (typeof SchoolTypes)[number] | null,
        [Validators.required],
      ],
      levels: new FormControl<string[]>([], {
        nonNullable: true,
        validators: [requiredStringArray],
      }),
      levelDescriptions: new FormControl<string[]>([], { nonNullable: true }),
      countEmployees: [
        null as number | null,
        [Validators.required, Validators.min(0)],
      ],
      countStudents: [
        null as number | null,
        [Validators.required, Validators.min(0)],
      ],
      schoolZipcode: [
        '',
        [Validators.required, Validators.pattern(SWISS_POSTAL_CODE_PATTERN)],
      ],
      schoolCity: [{ value: '', disabled: true }, [Validators.required]],
      schoolStreet: [''],
      schoolStreetNumber: [''],
      website: ['', [optionalWebsiteUrlValidator()]],
      about: ['', [Validators.required]],
      // Name + E-Mail werden aus dem Profil bzw. Firebase-Account übernommen
      // und dürfen vom Nutzer nicht editiert werden — analog zum Onboarding.
      contactName: [{ value: '', disabled: true }, [Validators.required]],
      contactEmail: [
        { value: '', disabled: true },
        [Validators.required, Validators.email],
      ],
      contactPhone: [''],
    },
    { validators: [requiredRawCity] }
  );

  /**
   * Kontaktperson Name + E-Mail werden immer aus Profil / Firebase-Account
   * gespiegelt — die Felder sind disabled, der Nutzer kann sie nicht
   * editieren. Immer überschreiben, damit die Ansprechperson aktuell bleibt.
   */
  private applyDefaultContactPersonFields(
    profile: Profile | null = null
  ): void {
    const fromProfile = profile?.name?.trim();
    if (fromProfile) {
      this.schoolForm.controls.contactName.setValue(fromProfile);
    }
    const e =
      this.auth.currentUser?.email?.trim() || profile?.email?.trim() || '';
    if (e) {
      this.schoolForm.controls.contactEmail.setValue(e);
    }
  }

  ngOnInit(): void {
    // Async options
    this.attributesApi.getInstitutions().subscribe(list => {
      this.institutions = list;
    });

    this.schoolForm.controls.schoolZipcode.disable();

    merge(
      this.schoolForm.valueChanges,
      this.schoolForm.controls.schoolZipcode.valueChanges
    )
      .pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitStep4Draft());

    queueMicrotask(() => {
      const d = this.initialDraft();
      if (d?.form) {
        this.schoolForm.patchValue(d.form as Record<string, unknown>);
        if (d.schoolLocation) {
          this.schoolLocation = d.schoolLocation;
          this.schoolForm.controls.schoolCity.setValue(
            d.schoolLocation.city || ''
          );
        }
      }
      this.applyDefaultContactPersonFields();
      this.profileService
        .getMe(false)
        .pipe(
          take(1),
          catchError(() => of(null as Profile | null))
        )
        .subscribe(p => {
          this.applyDefaultContactPersonFields(p);
        });
    });
  }

  addressComplete = signal(false);
  private addressField = viewChild(AddressAutocompleteFieldComponent);

  onSchoolLocationResolved(location: GeoLocation): void {
    this.schoolLocation = { ...this.schoolLocation, ...location };
    this.schoolForm.patchValue({
      schoolZipcode: this.schoolLocation.zipcode || '',
      schoolCity: this.schoolLocation.city || '',
      schoolStreet: this.schoolLocation.street || '',
      schoolStreetNumber: this.schoolLocation.streetNumber || '',
    });
  }

  private emitStep4Draft(): void {
    this.step4DraftChange.emit({
      form: this.schoolForm.getRawValue() as Record<string, unknown>,
      schoolLocation: this.schoolLocation,
    });
  }

  onBackClick(): void {
    this.emitStep4Draft();
    this.goBack.emit();
  }

  onContinue(): void {
    if (
      !revealInvalidFields([this.schoolForm, this.addressField()], this.host) ||
      !this.addressComplete()
    ) {
      return;
    }
    this.emitStep4Draft();
    this.formSubmitted.emit({
      form: this.schoolForm.getRawValue(),
      location: this.schoolLocation,
    });
  }
}
